import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ShieldCheck } from 'lucide-react';
import LoginRedirect from '../../components/LoginRedirect';
import botspotLogo from '../../assets/botspot_logo_horizontal.png';
import './AuthFirstSalesPage.css';
import TacticalLedger from './authFirstDesigns/TacticalLedger';

type AuthFirstSalesPageProps = {
  analyticsContext?: Record<string, unknown>;
  entryMode?: 'discovery' | 'checkout' | 'login' | 'signup';
  onAuthenticated?: () => void;
  presentation?: 'page' | 'overlay';
  returnTo: string;
};

const desktopProductSubheading = (
  "Chat with BotSpot's AI Agent to build and refine your strategy, run historical backtests, and connect a broker when you're ready."
);

const mobileProductSubheading = (
  'Chat with AI to build and backtest your trading strategy.'
);

const getMobileLayoutPreference = (): boolean => (
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  Boolean(window.matchMedia('(max-width: 960px)')?.matches)
);

const AuthFirstSalesPage = ({
  analyticsContext,
  entryMode = 'discovery',
  onAuthenticated,
  presentation = 'page',
  returnTo,
}: AuthFirstSalesPageProps): React.ReactElement => {
  const isMobileLayout = getMobileLayoutPreference();
  const productSubheading = isMobileLayout
    ? mobileProductSubheading
    : desktopProductSubheading;

  const isCheckoutEntry = entryMode === 'checkout';
  const isLoginEntry = entryMode === 'login';
  const isSignupEntry = entryMode === 'signup';
  const navAction = isCheckoutEntry ? 'Continue' : isLoginEntry ? 'Sign in' : 'Create account';
  const authHeading = isCheckoutEntry
    ? 'Create or sign in to continue'
    : isLoginEntry
      ? 'Sign in to BotSpot'
      : 'Create your BotSpot account';
  const authDescription = isCheckoutEntry
    ? 'Use Google or email. Your plan is saved.'
    : isLoginEntry
      ? 'Use Google or email.'
      : 'Use Google or email.';
  const authSource = isCheckoutEntry
    ? 'checkout_auth_first'
    : isLoginEntry
      ? 'login_auth_first'
      : isSignupEntry
        ? 'signup_auth_first'
        : 'sales_auth_first';
  const assuranceCopy = isCheckoutEntry
    ? 'Nothing is charged on this page.'
    : isSignupEntry
      ? 'Creating an account does not connect a broker or place a trade.'
      : 'Signing in does not connect a broker or place a trade.';
  const isOverlay = presentation === 'overlay';
  const subheading = (
    <p className="auth-first-sales__subheading">
      {productSubheading}
    </p>
  );

  const authContent = (
    <aside
      aria-label={isCheckoutEntry
        ? 'Create or sign in to continue'
        : isLoginEntry
          ? 'Sign in to BotSpot'
          : 'Create your BotSpot account'}
      className="auth-first-sales__conversion"
      id="auth-card-section"
    >
      <div className="auth-first-sales__auth-card">
        <LoginRedirect
          analyticsContext={analyticsContext}
          descriptionOverride={authDescription}
          headingOverride={authHeading}
          onAuthenticated={onAuthenticated}
          returnToOverride={returnTo}
          sourceOverride={authSource}
        />
      </div>

      <div className="auth-first-sales__assurance">
        <ShieldCheck aria-hidden="true" focusable="false" />
        <p>{assuranceCopy}</p>
      </div>

      {isCheckoutEntry ? (
        <div className="auth-first-sales__checkout-context">
          <a className="auth-first-sales__pricing-link" href="/pricing">
            Return to pricing
          </a>
        </div>
      ) : null}

    </aside>
  );

  return (
    <div
      className={`auth-first-sales auth-page bsui-scope${isOverlay ? ' auth-first-sales--overlay' : ''}`}
      data-bs-theme="dark"
      data-design="tactical-ledger"
      data-entry-mode={entryMode}
      data-presentation={presentation}
    >
      <Helmet>
        <title>
          {isCheckoutEntry
            ? 'Create Your BotSpot Account | Continue to Checkout'
            : isLoginEntry
              ? 'Sign In to BotSpot | Review the Product'
              : 'BotSpot | Build and Backtest a Trading Bot with AI'}
        </title>
        <meta
          name="description"
          content={isCheckoutEntry
            ? 'Create or sign in to BotSpot, then return to checkout with your plan saved.'
            : "Build a trading strategy with BotSpot's AI Agent, backtest it on historical data, and connect a broker when you're ready."}
        />
        {entryMode === 'discovery' && (
          <link rel="canonical" href="https://botspot.trade/sales" />
        )}
        <meta name="theme-color" content="#080b18" />
      </Helmet>

      {!isOverlay && (
        <header className="auth-first-nav">
          <div className="auth-first-nav__inner">
            <a aria-label="BotSpot home" className="auth-first-nav__logo" href="/">
              <img alt="BotSpot by LumiWealth" height={429} src={botspotLogo} width={1385} />
            </a>
            <a className="auth-first-nav__action" href="#auth-card-section">
              {navAction}
            </a>
          </div>
        </header>
      )}

      <main
        aria-labelledby="auth-first-title"
        className="auth-first-sales__container"
      >
        <TacticalLedger
          authContent={authContent}
          subheading={subheading}
          titleId="auth-first-title"
        />
      </main>

      {!isOverlay && (
        <footer className="auth-first-sales__footer">
          <p>Trading involves risk. Backtests and paper trading do not guarantee future results.</p>
        </footer>
      )}

    </div>
  );
};

export default AuthFirstSalesPage;
